import type {
  CommonElementData,
  ElementModality,
  ElementType,
  ImageFormState,
  ImagePrompt,
  ImageRange,
  MaterialV1Component,
  TextFormState,
  VideoFormState,
  VideoPrompt,
  ViduV1Element,
} from '@/app/types';

// 把接口返回的 components 数据格式转换为前端用的 assets 格式 (ImagePrompt | VideoPrompt)
export const convertComponents2Assets = (components?: MaterialV1Component[]): (ImagePrompt | VideoPrompt | null)[] => {
  return (
    components?.map((p) => {
      if (p.content_type === 'video') {
        return {
          video: p.content,
          videoPreview: p.content,
          coverImage: p.cover_uri,
          coverImagePreview: p.cover_uri,
        } as VideoPrompt;
      }

      const sourceImage = p.src_img || p.content;
      let selectedImageRange: ImageRange | undefined;
      if (p.selected_region) {
        selectedImageRange = [
          p.selected_region.top_left?.x ?? 0,
          p.selected_region.top_left?.y ?? 0,
          p.selected_region.bottom_right?.x ?? 0,
          p.selected_region.bottom_right?.y ?? 0,
        ];
      }

      return {
        sourceImage,
        sourceImagePreview: sourceImage,
        selectedImageRange: selectedImageRange,
        selectedImage: p.content,
        selectedImagePreview: p.content,
      } as ImagePrompt;
    }) || []
  );
};

export const convertAssets2Components = (assets?: (ImagePrompt | VideoPrompt | null)[]): MaterialV1Component[] => {
  if (!assets) {
    return [];
  }

  const components: MaterialV1Component[] = [];

  assets.forEach((p, idx) => {
    if (!p) {
      return;
    }

    const type = idx === 0 ? 'main' : ('auxiliary' as const);

    // 处理 ImagePrompt
    if ('sourceImage' in p) {
      components.push({
        type,
        content: p.selectedImage,
        src_img: p.sourceImage,
        content_type: 'image',

        // 有选择区域时，需要传裁剪的范围
        ...(p?.selectedImageRange?.length
          ? {
              selected_region: {
                top_left: {
                  x: p?.selectedImageRange?.[0],
                  y: p?.selectedImageRange?.[1],
                },
                bottom_right: {
                  x: p?.selectedImageRange?.[2],
                  y: p?.selectedImageRange?.[3],
                },
              },
            }
          : {}),
      });
      return;
    }

    // 处理 VideoPrompt
    components.push({
      type,
      content: p.video,
      content_type: 'video',
      cover_uri: p.coverImage,
    });
  });

  return components;
};

/** 将 ViduV1Element 转换为 ElementType */
export function convertViduV1ElementToElementType(element: ViduV1Element): ElementType {
  let modality: ElementModality = 'text';

  if (element.modality && element.modality !== 'unspecified') {
    modality = element.modality;
  } else if (element.components && element.components.length > 0) {
    // 根据 components 中的 content_type 判断 modality
    const hasVideo = element.components.some((c) => c.content_type === 'video');
    modality = hasVideo ? 'video' : 'image';
  }

  const baseData = {
    ...element,
    tag: undefined, // tag 字段在 CommonElementData 中是可选的
  };

  if (modality === 'text') {
    return {
      ...baseData,
      modality: 'text',
      description: element.recaption?.description,
    } as CommonElementData & TextFormState;
  }

  const optionalAssets = convertComponents2Assets(element.components);
  const { 0: first, 1: second, 2: third } = optionalAssets;

  if (modality === 'video') {
    return {
      ...baseData,
      modality: 'video',
      optionalAssets: [first || null, second || null, third || null],
      style: element.recaption?.style,
      description: element.recaption?.description,
    } as CommonElementData & VideoFormState;
  }

  // image 模式
  return {
    ...baseData,
    modality: 'image',
    optionalAssets: [first || null, second || null, third || null],
    voice: element.voice,
    style: element.recaption?.style,
    description: element.recaption?.description,
  } as CommonElementData & ImageFormState;
}
